/**
 * CloudWatch RUM telemetry wrapper — Story #3291.
 *
 * Replaces the previous Sentry SDK integration. CloudWatch RUM is AWS-native
 * and pinned to ap-southeast-2 (Sydney), satisfying the Australian
 * data-residency requirement.
 *
 * Usage:
 *   import { initRUM } from '@secureembed/shared-crm';
 *   initRUM('INNOVATEFLOW_CONFIG');   // before ReactDOM.createRoot
 *
 * The call is a no-op until window.<configKey>.RUM_APP_MONITOR_ID is
 * provisioned by the hosting Terraform layer, so apps work normally
 * before infra rolls out.
 *
 * Privacy posture: cookies enabled (anonymous RUM session-correlation only —
 * see note below), HTTP telemetry disabled (URL paths can leak structural
 * info like /insurance/claim/CLM-123), X-Ray disabled. Only errors + page-
 * load performance metrics are collected.
 *
 * Note on allowCookies=true: Story #3291 Round 2 CoWork browser smoke caught
 * that `allowCookies=false` triggers a SessionManager init race in the SDK
 * (v1.25.0) — the first dispatch carries a malformed sessionId and the RUM
 * dataplane returns 400 ValidationException. The RUM cookie is an anonymous,
 * HttpOnly, SameSite-Strict session correlation ID scoped to the AppMonitor's
 * own domain — it contains no PII and is not used for cross-site tracking.
 * The privacy-relevant leak vectors (URL paths, HTTP requests, X-Ray traces)
 * are all still off.
 */

import { AwsRum, AwsRumConfig } from 'aws-rum-web';

type ConfigKey = 'INNOVATEFLOW_CONFIG' | 'SECUREEMBED_CONFIG';

interface PortalConfig {
  RUM_APP_MONITOR_ID?: string;
  RUM_IDENTITY_POOL_ID?: string;
  RUM_GUEST_ROLE_ARN?: string;
  VERSION?: string;
  ENVIRONMENT?: string;
  [key: string]: unknown;
}

const RUM_REGION = 'ap-southeast-2';
const RUM_ENDPOINT = `https://dataplane.rum.${RUM_REGION}.amazonaws.com`;

// Story #3291 Round 4 diagnostic — unique tag so CoWork can grep console for the
// init path. All log lines start with [RUM-3291] so they're searchable. Also
// mirrors every diagnostic line to window.__rumDiag (an in-memory array) because
// both apps' Vite configs strip console.* in prod via `esbuild.drop=['console']`.
// window.__rumDiag survives the drop and can be inspected by CoWork puppeteer
// via document evaluation or DevTools.
const LOG_TAG = '[RUM-3291]';

interface RumDiagEntry {
  ts: string;
  level: 'info' | 'warn' | 'error';
  msg: string;
  data?: unknown;
}

function diag(level: RumDiagEntry['level'], msg: string, data?: unknown): void {
  const entry: RumDiagEntry = { ts: new Date().toISOString(), level, msg, data };
  try {
    const w = window as unknown as { __rumDiag?: RumDiagEntry[] };
    if (!w.__rumDiag) w.__rumDiag = [];
    w.__rumDiag.push(entry);
    // Mirror into a data attribute on <html> so CoWork can read it via DOM
    // inspection (no JS-eval needed). Keep only the last 32 entries to bound size.
    const tail = w.__rumDiag.slice(-32);
    document.documentElement.setAttribute('data-rum-diag', JSON.stringify(tail));
  } catch {
    // Defensive — should never throw, but never let diagnostics break init
  }
  // eslint-disable-next-line no-console
  (console[level] as (...a: unknown[]) => void)(LOG_TAG, msg, data ?? '');
}

/**
 * Clear cached aws-rum-web SDK state from localStorage. The SDK caches Cognito
 * guest credentials under cwr_* keys; cached creds from failed prior flows can
 * mask the real init state and cause the SDK to skip Cognito federation
 * entirely (which is what CoWork browser smoke Rounds 2-4 observed).
 *
 * This runs on every initRUM call so each page load forces a fresh credential
 * fetch. The privacy cost is zero — the cleared values were anonymous session
 * tokens, not user-identifying data.
 */
function clearStaleSdkCache(): string[] {
  const cleared: string[] = [];
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('cwr_') || k.startsWith('aws-rum-'))) {
        toRemove.push(k);
      }
    }
    for (const k of toRemove) {
      localStorage.removeItem(k);
      cleared.push(k);
    }
  } catch {
    // localStorage may be unavailable (private browsing, etc) — non-fatal
  }
  return cleared;
}

let _rum: AwsRum | undefined;

export function initRUM(configKey: ConfigKey = 'INNOVATEFLOW_CONFIG'): AwsRum | undefined {
  diag('info', 'initRUM start', { configKey });
  if (_rum) {
    diag('info', 'already initialized, returning cached instance');
    return _rum;
  }

  const config = (window as unknown as Record<string, unknown>)[configKey] as PortalConfig | undefined;
  const appMonitorId = config?.RUM_APP_MONITOR_ID;
  const identityPoolId = config?.RUM_IDENTITY_POOL_ID;
  const guestRoleArn = config?.RUM_GUEST_ROLE_ARN;
  const release = config?.VERSION ?? '0.0.0';

  diag('info', 'config read', {
    has_appMonitorId: !!appMonitorId,
    has_identityPoolId: !!identityPoolId,
    has_guestRoleArn: !!guestRoleArn,
    appMonitorId_prefix: appMonitorId?.slice(0, 8),
    identityPoolId_prefix: identityPoolId?.split(':')[0],
    release,
    region: RUM_REGION,
  });

  if (!appMonitorId || !identityPoolId) {
    diag('warn', 'missing required config (appMonitorId or identityPoolId) — aborting init');
    return undefined;
  }

  const cleared = clearStaleSdkCache();
  diag('info', 'cleared stale localStorage keys', { count: cleared.length, keys: cleared });

  const rumConfig: AwsRumConfig = {
    sessionSampleRate: 1,
    guestRoleArn,
    identityPoolId,
    endpoint: RUM_ENDPOINT,
    telemetries: ['errors', 'performance'],
    allowCookies: true,
    enableXRay: false,
  };

  diag('info', 'constructing AwsRum(appMonitorId, release, region, config)');
  try {
    _rum = new AwsRum(appMonitorId, release, RUM_REGION, rumConfig);
    diag('info', 'AwsRum constructed OK');

    // Seed the event buffer so the first auto-dispatch is never empty.
    // aws-rum-web v1.25 returns 400 ValidationException from the dataplane if
    // the RumEvents array is empty — this guards against any race where the
    // dispatcher fires before the SDK has buffered its session_start/page_view.
    try {
      _rum.recordEvent('rum_init', { ts: new Date().toISOString(), version: release });
      diag('info', 'recordEvent(rum_init) buffered OK');
    } catch (seedErr) {
      diag('warn', 'recordEvent(rum_init) failed (non-fatal)', { error: String(seedErr) });
    }

    return _rum;
  } catch (err) {
    diag('error', 'AwsRum construction threw — RUM is DISABLED for this session', { error: String(err) });
    return undefined;
  }
}

export function getRUM(): AwsRum | undefined {
  return _rum;
}

export function __resetRumForTesting(): void {
  _rum = undefined;
}
